import { SalesItemRecord } from '@navio/nsdk';
type TireWithAttributes = {
  tire: {
    wetGripClass?: string | null;
    rollingResistanceClass?: string | null;
    soundDb?: number | null;
  };
};

type TireWithLabel = TireWithAttributes & {
  imageLabel?: {
    url?: string | null;
  } | null;
};

export const getShowEuLabel = (tireFromInventorySupplier: TireWithLabel) => {
  const { tire, imageLabel } = tireFromInventorySupplier;
  return !!imageLabel?.url || tire.wetGripClass || tire.rollingResistanceClass || tire.soundDb;
};

export const getHasTireAttributesToShow = (tireFromInventorySupplier: TireWithAttributes) => {
  const { tire } = tireFromInventorySupplier;
  return tire.wetGripClass || tire.rollingResistanceClass || tire.soundDb;
};

/**
 * Compares two grade values and returns the better one.
 * For EU tire labels, 'A' is the best grade, followed by B, C, D, E, F, G.
 * Returns the better grade, or the first non-null value if one is null.
 */
function getBetterGrade(
  grade1: string | null | undefined,
  grade2: string | null | undefined
): string | null | undefined {
  if (!grade1) {
    return grade2;
  }
  if (!grade2) {
    return grade1;
  }
  // Compare alphabetically - lower letter (A) is better than higher letter (B, C, etc.)
  return grade1 < grade2 ? grade1 : grade2;
}

/**
 * Determines which tire (front or rear) has better attributes based on EU tire label grading.
 * Returns the attributes from the tire with better overall grades.
 */
export const getBestTireToDisplay = <T extends TireWithAttributes>({
  frontTireFromInventorySupplier,
  rearTireFromInventorySupplier
}: {
  frontTireFromInventorySupplier: T;
  rearTireFromInventorySupplier: T;
}): T => {
  const {
    wetGripClass: wetGripClassFront,
    rollingResistanceClass: rollingResistanceClassFront,
    soundDb: soundDbFront
  } = frontTireFromInventorySupplier.tire;
  const {
    wetGripClass: wetGripClassRear,
    rollingResistanceClass: rollingResistanceClassRear,
    soundDb: soundDbRear
  } = rearTireFromInventorySupplier.tire;

  // Compare wet grip class (lower letter is better)
  const betterWetGrip = getBetterGrade(wetGripClassFront, wetGripClassRear);
  const frontHasBetterWetGrip =
    betterWetGrip === wetGripClassFront && wetGripClassFront !== null && wetGripClassFront !== undefined;
  const rearHasBetterWetGrip =
    betterWetGrip === wetGripClassRear && wetGripClassRear !== null && wetGripClassRear !== undefined;

  // Compare rolling resistance class (lower letter is better)
  const betterRollingResistance = getBetterGrade(rollingResistanceClassFront, rollingResistanceClassRear);
  const frontHasBetterRollingResistance =
    betterRollingResistance === rollingResistanceClassFront &&
    rollingResistanceClassFront !== null &&
    rollingResistanceClassFront !== undefined;
  const rearHasBetterRollingResistance =
    betterRollingResistance === rollingResistanceClassRear &&
    rollingResistanceClassRear !== null &&
    rollingResistanceClassRear !== undefined;

  // Compare sound dB (lower number is better)
  // eslint-disable-next-line no-restricted-syntax -- flag set inside the sound-dB comparison branches
  let frontHasBetterSound = false;
  // eslint-disable-next-line no-restricted-syntax -- flag set inside the sound-dB comparison branches
  let rearHasBetterSound = false;
  if (soundDbFront !== null && soundDbFront !== undefined && soundDbRear !== null && soundDbRear !== undefined) {
    if (soundDbFront < soundDbRear) {
      frontHasBetterSound = true;
    } else if (soundDbRear < soundDbFront) {
      rearHasBetterSound = true;
    }
    // If equal, neither is better
  } else if (soundDbFront !== null && soundDbFront !== undefined) {
    // Front has value, rear doesn't - front is better
    frontHasBetterSound = true;
  } else if (soundDbRear !== null && soundDbRear !== undefined) {
    // Rear has value, front doesn't - rear is better
    rearHasBetterSound = true;
  }

  // Count which tire has more better attributes
  const frontBetterCount = [frontHasBetterWetGrip, frontHasBetterRollingResistance, frontHasBetterSound].filter(
    Boolean
  ).length;
  const rearBetterCount = [rearHasBetterWetGrip, rearHasBetterRollingResistance, rearHasBetterSound].filter(
    Boolean
  ).length;

  // Return the tire with better overall attributes (default to front if equal)
  const shouldUseFront = frontBetterCount >= rearBetterCount;

  return shouldUseFront ? frontTireFromInventorySupplier : rearTireFromInventorySupplier;
};

export function getQuantityOptionsWithTrans({
  quantityMax,
  quantityMin = 1,
  translations
}: {
  quantityMax: number;
  quantityMin?: number;
  translations: { pcs: string };
}) {
  const start = Math.max(1, quantityMin);
  const length = Math.max(0, quantityMax - start + 1);

  return Array.from({ length }, (_, i) => ({
    label: `${start + i} ${translations.pcs}`,
    value: start + i
  }));
}

export const getGenericItemsGrossAmount = (genericItems: SalesItemRecord[]) =>
  genericItems.reduce((sum, item) => sum + item.price.priceGross.amount, 0);
